/**
 * Authentication Service
 * Handles OAuth flows, error differentiation, and loading states
 */

import FirebaseAuthService from './firebaseAuthService';
import AppleAuthService from './appleAuthService';
import { OAUTH_CLIENT_IDS } from '../config/runtimeConfig';

// OAuth Configuration
const OAUTH_CONFIG = {
  google: {
    clientId: OAUTH_CLIENT_IDS.google,
    redirectUri: `${window.location.origin}/auth/google/callback`,
    scope: 'openid email profile'
  },
  apple: {
    clientId: OAUTH_CLIENT_IDS.apple,
    redirectUri: `${window.location.origin}/auth/apple/callback`,
    scope: 'email name'
  }
};

// Error types for better user feedback
export const AUTH_ERROR_TYPES = {
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  EMAIL_NOT_FOUND: 'EMAIL_NOT_FOUND',
  WRONG_PASSWORD: 'WRONG_PASSWORD',
  ACCOUNT_LOCKED: 'ACCOUNT_LOCKED',
  SERVER_ERROR: 'SERVER_ERROR',
  NETWORK_ERROR: 'NETWORK_ERROR',
  OAUTH_ERROR: 'OAUTH_ERROR',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR'
};

// Error messages mapping
export const ERROR_MESSAGES = {
  [AUTH_ERROR_TYPES.INVALID_CREDENTIALS]: 'Invalid email or password. Please check your credentials and try again.',
  [AUTH_ERROR_TYPES.EMAIL_NOT_FOUND]: 'No account found with this email address. Please check your email or sign up for a new account.',
  [AUTH_ERROR_TYPES.WRONG_PASSWORD]: 'Incorrect password. Please try again or reset your password.',
  [AUTH_ERROR_TYPES.ACCOUNT_LOCKED]: 'Your account has been temporarily locked. Please contact support or try again later.',
  [AUTH_ERROR_TYPES.SERVER_ERROR]: 'Server error occurred. Please try again in a few moments.',
  [AUTH_ERROR_TYPES.NETWORK_ERROR]: 'Unable to connect to Hebrew Hearts. Please check your internet connection and try again.',
  [AUTH_ERROR_TYPES.OAUTH_ERROR]: 'Authentication failed. Please try again or use email login.',
  [AUTH_ERROR_TYPES.UNKNOWN_ERROR]: 'An unexpected error occurred. Please try again.'
};

const NETWORK_FAILURE_PATTERNS = [
  'failed to fetch',
  'networkerror',
  'load failed',
  'network request failed',
  'err_internet_disconnected',
  'err_connection_refused',
];

const getErrorMessage = (error) => {
  if (typeof error === 'string') return error;
  return error?.message;
};

const isNetworkFailure = (message) => (
  Boolean(message) && NETWORK_FAILURE_PATTERNS.some((pattern) => message.toLowerCase().includes(pattern))
);

/**
 * Parse error response to determine error type
 */
export const parseAuthError = (error) => {
  if (!error) return { type: AUTH_ERROR_TYPES.UNKNOWN_ERROR, message: ERROR_MESSAGES[AUTH_ERROR_TYPES.UNKNOWN_ERROR] };

  const message = getErrorMessage(error);

  // Network errors
  if (!navigator.onLine || isNetworkFailure(message)) {
    return { type: AUTH_ERROR_TYPES.NETWORK_ERROR, message: ERROR_MESSAGES[AUTH_ERROR_TYPES.NETWORK_ERROR] };
  }

  // Server response errors
  if (error.response) {
    const status = error.response.status;
    const data = error.response.data;

    switch (status) {
      case 401:
        if (data?.message?.includes('email') || data?.error?.includes('email')) {
          return { type: AUTH_ERROR_TYPES.EMAIL_NOT_FOUND, message: ERROR_MESSAGES[AUTH_ERROR_TYPES.EMAIL_NOT_FOUND] };
        }
        if (data?.message?.includes('password') || data?.error?.includes('password')) {
          return { type: AUTH_ERROR_TYPES.WRONG_PASSWORD, message: ERROR_MESSAGES[AUTH_ERROR_TYPES.WRONG_PASSWORD] };
        }
        return { type: AUTH_ERROR_TYPES.INVALID_CREDENTIALS, message: ERROR_MESSAGES[AUTH_ERROR_TYPES.INVALID_CREDENTIALS] };
      
      case 423:
        return { type: AUTH_ERROR_TYPES.ACCOUNT_LOCKED, message: ERROR_MESSAGES[AUTH_ERROR_TYPES.ACCOUNT_LOCKED] };
      
      case 500:
      case 502:
      case 503:
      case 504:
        return { type: AUTH_ERROR_TYPES.SERVER_ERROR, message: ERROR_MESSAGES[AUTH_ERROR_TYPES.SERVER_ERROR] };
      
      default:
        return { type: AUTH_ERROR_TYPES.UNKNOWN_ERROR, message: data?.message || ERROR_MESSAGES[AUTH_ERROR_TYPES.UNKNOWN_ERROR] };
    }
  }

  // Request errors (network issues)
  if (error.request) {
    return { type: AUTH_ERROR_TYPES.NETWORK_ERROR, message: ERROR_MESSAGES[AUTH_ERROR_TYPES.NETWORK_ERROR] };
  }

  // OAuth specific errors
  if (error.message?.includes('oauth') || error.message?.includes('OAuth')) {
    return { type: AUTH_ERROR_TYPES.OAUTH_ERROR, message: ERROR_MESSAGES[AUTH_ERROR_TYPES.OAUTH_ERROR] };
  }

  // Generic error handling — avoid exposing raw browser messages like "Failed to fetch"
  if (message && !isNetworkFailure(message)) {
    return { type: AUTH_ERROR_TYPES.UNKNOWN_ERROR, message };
  }

  return { type: AUTH_ERROR_TYPES.UNKNOWN_ERROR, message: ERROR_MESSAGES[AUTH_ERROR_TYPES.UNKNOWN_ERROR] };
};

/**
 * Initiate Google OAuth flow using Firebase
 */
export const initiateGoogleAuth = async () => {
  try {
    const result = await FirebaseAuthService.signInWithGoogle();
    
    // Dispatch success event with Firebase user data
    window.dispatchEvent(new CustomEvent('oauth-success', { 
      detail: { 
        provider: 'google', 
        userData: result.user,
        token: result.token
      } 
    }));
    
    return result;
  } catch (error) {
    console.error('Google OAuth initiation failed:', error);
    const code = error?.code ? ` (${error.code})` : '';
    const detail = error?.message || 'Unknown error';

    window.dispatchEvent(new CustomEvent('oauth-error', {
      detail: {
        provider: 'google',
        error: detail,
        code: error?.code || null,
      },
    }));

    throw new Error(`Failed to initiate Google authentication${code}: ${detail}`);
  }
};

/**
 * Initiate Apple OAuth flow using Apple Sign-In
 */
export const initiateAppleAuth = async () => {
  console.log('AuthService.initiateAppleAuth called');
  try {
    const result = await AppleAuthService.signInWithApple();

    // Send the authorization code to backend for verification and token exchange
    if (result.code) {
      try {
        const response = await fetch(`${API_BASE_URL}/auth/apple/callback`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ code: result.code })
        });

        if (!response.ok) {
          throw new Error('Failed to authenticate with backend');
        }

        const backendData = await response.json();

        // Dispatch success event with backend-verified user data
        window.dispatchEvent(new CustomEvent('oauth-success', {
          detail: {
            provider: 'apple',
            userData: backendData.user,
            token: backendData.token,
            isNewUser: backendData.isNewUser
          }
        }));

        return backendData;
      } catch (error) {
        console.error('Backend Apple auth failed:', error);
        throw new Error('Failed to complete Apple authentication');
      }
    } else {
      throw new Error('No authorization code received from Apple');
    }
  } catch (error) {
    console.error('Apple OAuth initiation failed:', error);
    
    // Dispatch error event
    window.dispatchEvent(new CustomEvent('oauth-error', { 
      detail: { 
        provider: 'apple', 
        error: error.message 
      } 
    }));
    
    throw new Error('Failed to initiate Apple authentication');
  }
};

/**
 * Generate random state for OAuth security
 */
const generateState = () => {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};

/**
 * Handle OAuth callback success
 */
export const handleOAuthSuccess = (provider, userData) => {
  // Store OAuth success data
  localStorage.setItem('oauth_success', JSON.stringify({
    provider,
    userData,
    timestamp: Date.now()
  }));

  // Dispatch success event
  window.dispatchEvent(new CustomEvent('oauth-success', { 
    detail: { provider, userData } 
  }));
};

/**
 * Handle OAuth callback error
 */
export const handleOAuthError = (provider, error) => {
  console.error(`${provider} OAuth error:`, error);
  
  // Dispatch error event
  window.dispatchEvent(new CustomEvent('oauth-error', { 
    detail: { provider, error } 
  }));
};

/**
 * Check if OAuth is supported
 */
export const isOAuthSupported = () => {
  return !!(OAUTH_CONFIG.google.clientId && OAUTH_CONFIG.apple.clientId);
};

/**
 * Get OAuth provider display name
 */
export const getProviderDisplayName = (provider) => {
  const names = {
    google: 'Google',
    apple: 'Apple'
  };
  return names[provider] || provider;
};

/**
 * Authentication service with enhanced error handling
 */
export const AuthService = {
  // OAuth methods
  initiateGoogleAuth,
  initiateAppleAuth,
  handleOAuthSuccess,
  handleOAuthError,
  
  // Utility methods
  parseAuthError,
  isOAuthSupported,
  getProviderDisplayName,
  
  // Error constants
  ERROR_TYPES: AUTH_ERROR_TYPES,
  ERROR_MESSAGES
};

export default AuthService;
